import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import api from "./apiClient";

import cookiesUtility from "../utils/cookies.utility";

import { DeleteStudentParams, UpsertStudentParams } from "../interfaces/student";
import { EtyStudent } from "../interfaces/entities";

export const useGetInstanceStudents = () => {
  const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);

  const queryFn = async (): Promise<EtyStudent[]> => {
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    const response = await api.get("/students");
    return response.data;
  };

  return useQuery({
    queryKey: ["students"],
    queryFn,
    staleTime: Infinity,
    retry: 0,
    enabled: !!token
  });
};

export const useGetStudentDetails = (studentId: number) => {
  const queryFn = async (): Promise<EtyStudent> => {
    const token = cookiesUtility.getCookie(
      process.env.REACT_APP_AUTH_TOKEN_NAME as string
    );

    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    const response = await api.get(`/students/${studentId}`);
    return response.data;
  };

  return useQuery({
    queryKey: ["students", studentId],
    queryFn,
    staleTime: 0,
    retry: 0,
    enabled: !!studentId && studentId !== 0
  });
};

export const useSaveStudent = () => {
  const queryClient = useQueryClient();

  const mutationFn = async (params: UpsertStudentParams): Promise<EtyStudent> => {
    const token = cookiesUtility.getCookie(
      process.env.REACT_APP_AUTH_TOKEN_NAME as string
    );

    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    const payload = { ...params.payload };

    // if (payload.birth_date) {
    //   const [month, day, year] = (payload.birth_date as string).split("/");
    //   payload.birth_date = `${year}-${month}-${day}`;
    // }

    return await api.put(`/students`, payload);
  };

  return useMutation({
    mutationFn,
    onMutate: (variables) => {
      const { setIsProcessing } = variables;
      setIsProcessing(true);
    },
    onSuccess: (data, variables) => {
      const { successCallback } = variables;

      queryClient.invalidateQueries({ queryKey: ["students"] });

      if (successCallback) {
        successCallback(); // Call the success callback if it's provided
      }
    },
    onSettled: (data, error, variables) => {
      const { setIsProcessing } = variables;
      setIsProcessing(false);
    },
  });
};

export const useDeleteStudent = () => {
  const queryClient = useQueryClient();

  const mutationFn = async (params: DeleteStudentParams): Promise<void> => {
    const token = cookiesUtility.getCookie(
      process.env.REACT_APP_AUTH_TOKEN_NAME as string
    );

    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    await api.delete(`/students/${params.payload.id}`);
  };

  return useMutation({
    mutationFn,
    onMutate: (variables) => {
      const { setIsProcessing } = variables;
      setIsProcessing(true);
    },
    onSuccess: (data, variables) => {
      const { successCallback } = variables;

      queryClient.invalidateQueries({ queryKey: ["students"] });

      if (successCallback) {
        successCallback(); // Call the success callback if it's provided
      }
    },
    onSettled: (data, error, variables) => {
      const { setIsProcessing } = variables;
      setIsProcessing(false);
    },
  });
};
